import {
	keepPreviousData,
	useMutation,
	useQuery,
	useQueryClient,
} from '@tanstack/react-query'
import type { SyncChangeFilters } from '@/types/sync'
import { getSyncChanges, getSyncLogs, syncProtheus } from '../services/sync'

// ── Query keys ────────────────────────────────────────────────────────────────

export const syncKeys = {
	all: ['sync'] as const,
	logs: () => [...syncKeys.all, 'logs'] as const,
	changes: (logId: string, filters: SyncChangeFilters) =>
		[...syncKeys.all, 'changes', logId, filters] as const,
}

// ── GET /sync/logs ────────────────────────────────────────────────────────────

export function useSyncLogs() {
	return useQuery({
		queryKey: syncKeys.logs(),
		queryFn: getSyncLogs,
		staleTime: 30 * 1000, // 30 seconds – logs change after each sync
		// Sync agendado roda em background: acompanha enquanto houver log RUNNING
		refetchInterval: (query) =>
			query.state.data?.items.some((log) => log.status === 'RUNNING')
				? 5000
				: false,
	})
}

// ── GET /sync/logs/:id/changes ────────────────────────────────────────────────

export function useSyncChanges(
	logId: string | null,
	filters: SyncChangeFilters,
) {
	return useQuery({
		queryKey: syncKeys.changes(logId ?? '', filters),
		queryFn: () => getSyncChanges(logId as string, filters),
		enabled: !!logId,
		placeholderData: keepPreviousData,
	})
}

// ── POST /sync/protheus ───────────────────────────────────────────────────────

export function useSyncProtheus() {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: () => syncProtheus(),
		onSuccess: () => {
			// Refresh log list after a successful sync
			queryClient.invalidateQueries({ queryKey: syncKeys.logs() })
		},
	})
}
